import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import {
  useNotifications,
  type Notification,
} from "@/contexts/notification-context";
import { NotificationItem } from "@/components/notification-item";
import { getCurrentUser } from "@/lib/api/auth";
import {
  getNotificationActions,
  getPrimaryNotificationHref,
} from "@/lib/notification-actions";
import { Button } from "@/components/ui/button";
import { CustomSelect } from "@/components/ui/custom-select";
import { EmptyState, Text } from "@/components/ui/text";
import { ErrorBanner } from "@/components/ui/error-banner";
import { Icon } from "@/components/ui/icons";
import { PageHeader, PageHeaderActions } from "@/components/ui/page-header";
import { HStack } from "@/components/ui/h-stack";
import { PageTitle } from "@/contexts/page-title-context";
import { Skeleton } from "@/components/ui/skeleton";
import { Surface } from "@/components/ui/surface";
import { VStack } from "@/components/ui/v-stack";

type FilterType = "all" | "unread" | "read";
type CategoryFilter =
  | "All"
  | "Load"
  | "Trip"
  | "Trade"
  | "Ticket"
  | "Qr"
  | "System"
  | "Payment"
  | "Document";

function VirtualizedNotificationList({
  notifications,
  isAdmin,
  onNotificationClick,
  onMarkAsRead,
  navigate,
}: {
  notifications: Notification[];
  isAdmin: boolean;
  onNotificationClick: (n: Notification) => void;
  onMarkAsRead: (id: string) => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  "use no memo";
  const { t: tActions } = useTranslation("notifications.actions");
  const parentRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual returns non-memoizable functions; "use no memo" already opts out of React Compiler
  const virtualizer = useVirtualizer({
    count: notifications.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 88,
    overscan: 10,
    measureElement: (el) => el.getBoundingClientRect().height,
  });

  return (
    <VStack
      ref={parentRef}
      data-testid="notifications-list"
      className="flex-1 overflow-auto"
    >
      <VStack
        className="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const notification = notifications[virtualItem.index];
          const actions = getNotificationActions(notification, isAdmin);
          return (
            <VStack
              key={notification.id}
              ref={virtualizer.measureElement}
              data-index={virtualItem.index}
              className="absolute top-0 left-0 w-full"
              style={{
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <NotificationItem
                notification={notification}
                variant="card"
                onClick={() => onNotificationClick(notification)}
                data-testid={`notification-item-${notification.id}`}
                actions={
                  actions.length > 0
                    ? actions.map((action, i) => (
                        <Button
                          key={action.href}
                          color={i === 0 ? "primary" : "neutral"}
                          variant={i === 0 ? "solid" : "outline"}
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!notification.is_read) {
                              onMarkAsRead(notification.id);
                            }
                            void navigate({ to: action.href as never });
                          }}
                          data-testid={`notification-action-${notification.id}-${i}`}
                        >
                          {tActions(action.label)}
                        </Button>
                      ))
                    : undefined
                }
              />
            </VStack>
          );
        })}
      </VStack>
    </VStack>
  );
}

function NotificationsPage() {
  const navigate = useNavigate();
  const { t: tCommon } = useTranslation("common");
  const { t: tNotifications } = useTranslation("notifications");
  const { t: tNotificationFilters } = useTranslation("notifications.filters");
  const { t: tNotificationCategories } = useTranslation(
    "notifications.categories",
  );
  const { t: tNotificationEmpty } = useTranslation("notifications.empty");
  const { t: tSettings } = useTranslation("settings");
  const isAdmin = getCurrentUser()?.is_admin === true;
  const {
    notifications,
    loading,
    isError,
    markAsRead,
    markAllAsRead,
    unreadCount,
    fetchNotifications,
  } = useNotifications();
  const [filter, setFilter] = useState<FilterType>("all");
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>("All");
  const hasActiveFilters = filter !== "all" || categoryFilter !== "All";

  const statusOptions = [
    { value: "all", label: tNotificationFilters("allStatus") },
    { value: "unread", label: tNotificationFilters("unread") },
    { value: "read", label: tNotificationFilters("read") },
  ];

  const categoryOptions = [
    { value: "All", label: tNotificationFilters("allCategory") },
    { value: "Load", label: tNotificationCategories("load") },
    { value: "Trip", label: tNotificationCategories("trip") },
    { value: "Trade", label: tNotificationCategories("trade") },
    { value: "Ticket", label: tNotificationCategories("ticket") },
    { value: "Qr", label: tNotificationCategories("qr") },
    { value: "System", label: tNotificationCategories("system") },
    { value: "Payment", label: tNotificationCategories("payment") },
    { value: "Document", label: tNotificationCategories("document") },
  ];

  const filteredNotifications = notifications.filter((n) => {
    if (filter === "unread" && n.is_read) return false;
    if (filter === "read" && !n.is_read) return false;
    if (categoryFilter !== "All" && n.category !== categoryFilter) return false;
    return true;
  });

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    const href = getPrimaryNotificationHref(notification, isAdmin);
    if (href) {
      void navigate({ to: href as never });
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  return (
    <VStack gap="lg" data-testid="notifications-page">
      <PageHeader data-testid="notifications-header">
        <PageTitle>{tSettings("notifications")}</PageTitle>
        {unreadCount > 0 && (
          <PageHeaderActions>
            <Button
              color="neutral"
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              data-testid="notifications-mark-all-read"
            >
              <Icon.Check size="sm" className="mr-1" />
              {tNotifications("markAllAsRead")} ({unreadCount})
            </Button>
          </PageHeaderActions>
        )}
      </PageHeader>

      <HStack align="end" gap="xl" data-testid="notifications-filters">
        <VStack gap="none">
          <Text size="label" weight="medium" color="muted" className="mb-1.5">
            {tNotificationFilters("statusLabel")}
          </Text>
          <CustomSelect
            value={filter}
            options={statusOptions}
            onChange={(v) => setFilter(v as FilterType)}
            size="sm"
            data-testid="notifications-status-select"
            label={tNotificationFilters("statusLabel")}
          />
        </VStack>
        <VStack gap="none">
          <Text size="label" weight="medium" color="muted" className="mb-1.5">
            {tNotificationFilters("categoryLabel")}
          </Text>
          <CustomSelect
            value={categoryFilter}
            options={categoryOptions}
            onChange={(v) => setCategoryFilter(v as CategoryFilter)}
            size="sm"
            data-testid="notifications-category-select"
            label={tNotificationFilters("categoryLabel")}
          />
        </VStack>
        {hasActiveFilters && (
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setFilter("all");
              setCategoryFilter("All");
            }}
            data-testid="notifications-clear-filters"
          >
            {tNotificationFilters("clearFilters")}
          </Button>
        )}
      </HStack>

      {isError && (
        <ErrorBanner
          title={tCommon("errorLoadingData")}
          onRetry={() => void fetchNotifications()}
          retryLabel={tCommon("retry")}
        />
      )}

      {loading ? (
        <VStack data-testid="notifications-loading">
          {Array.from({ length: 5 }).map((_, index) => (
            <Surface
              key={index}
              bg="surface"
              border="default"
              rounded="xl"
              padding="lg"
              className="border-l-4 border-l-[color:var(--outline-variant)]"
            >
              <HStack align="stretch" gap="lg">
                <Skeleton rounded="full" className="h-10 w-10 flex-shrink-0" />
                <VStack fill gap="none">
                  <HStack
                    align="start"
                    justify="between"
                    gap="lg"
                    className="mb-2"
                  >
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-4 w-20" />
                  </HStack>
                  <Skeleton className="h-4 w-full" />
                </VStack>
              </HStack>
            </Surface>
          ))}
        </VStack>
      ) : filteredNotifications.length === 0 ? (
        <EmptyState
          icon={<Icon.Bell size="xl" color="muted" />}
          title={
            notifications.length === 0
              ? tNotifications("noNotificationsYet")
              : filter === "unread" && categoryFilter === "All"
                ? tNotificationEmpty("noUnreadTitle")
                : tNotificationEmpty("noResultsTitle")
          }
          description={
            notifications.length === 0
              ? tNotificationEmpty("noNotificationsDescription")
              : filter === "unread" && categoryFilter === "All"
                ? tNotificationEmpty("noUnreadDescription")
                : tNotificationEmpty("noResultsDescription")
          }
          action={
            hasActiveFilters ? (
              <Button
                color="neutral"
                variant="outline"
                onClick={() => {
                  setFilter("all");
                  setCategoryFilter("All");
                }}
              >
                {tNotificationFilters("clearFilters")}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <VirtualizedNotificationList
          notifications={filteredNotifications}
          isAdmin={isAdmin}
          onNotificationClick={handleNotificationClick}
          onMarkAsRead={markAsRead}
          navigate={navigate}
        />
      )}
    </VStack>
  );
}

export const Route = createFileRoute("/_app/notifications")({
  component: NotificationsPage,
});
